import * as actionTypes from './actionTypes';
import { EmployeeActionTypes } from './actionTypes';
import { HttpError } from '../../config/Axios/axios-instance';
import { Employee } from '../../domain/Employee';
import { ListResults } from '../../common/List/List';

export type EmployeeStateType = {
  employeesList: ListResults<Employee> | null;
  employeesLoading: boolean;
  employeesError: HttpError;
  employeesListUpdateNeeded: boolean;
  employeeCreateLoading: boolean;
  employeeCreateError: HttpError;
  employeeCreateSuccess: boolean;
  createdEmployee: Employee | null;
  employeeUpdateLoading: boolean;
  employeeUpdateError: HttpError;
  employeeUpdateSuccess: boolean;
  employeeDeleteLoading: boolean;
  employeeDeleteError: HttpError;
  employeeDeleteSuccess: boolean;
  employee: Employee | null;
  employeeLoading: boolean;
  employeeError: HttpError;
  allEmployees: Employee[];
  allEmployeesLoading: boolean;
  allEmployeesError: HttpError;
};

export type EmployeeActionType = EmployeeStateType & {
  type: EmployeeActionTypes;
};

export const initialState: EmployeeStateType = {
  employeesList: null,
  employeesLoading: true,
  employeesError: null,
  employeesListUpdateNeeded: false,
  employeeCreateLoading: false,
  employeeCreateError: null,
  employeeCreateSuccess: false,
  createdEmployee: null,
  employeeUpdateLoading: false,
  employeeUpdateError: null,
  employeeUpdateSuccess: false,
  employeeDeleteLoading: false,
  employeeDeleteError: null,
  employeeDeleteSuccess: false,
  employee: null,
  employeeLoading: false,
  employeeError: null,
  allEmployees: [],
  allEmployeesError: null,
  allEmployeesLoading: false,
};

const fetchEmployeesStart = (state: EmployeeStateType): EmployeeStateType => ({
  ...state,
  employeesLoading: true,
});

const fetchEmployeesSuccess = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  employeesList: action.employeesList,
  employeesLoading: false,
  employeesError: null,
  employeesListUpdateNeeded: false,
});

const fetchEmployeesFail = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  employeesError: action.employeesError,
  employeesLoading: false,
});

const fetchAllEmployeesStart = (
  state: EmployeeStateType,
): EmployeeStateType => ({
  ...state,
  allEmployeesLoading: true,
});

const fetchAllEmployeesSuccess = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  allEmployees: action.allEmployees,
  allEmployeesLoading: false,
  allEmployeesError: null,
});

const fetchAllEmployeesFail = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  allEmployeesError: action.allEmployeesError,
  allEmployeesLoading: false,
});

const fetchEmployeeStart = (state: EmployeeStateType): EmployeeStateType => ({
  ...state,
  employeeLoading: true,
  employeeCreateSuccess: false,
  employeeCreateError: null,
});

const fetchEmployeeSuccess = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  employee: action.employee,
  employeeLoading: false,
  employeeError: null,
});

const fetchEmployeeFail = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  employeeError: action.employeeError,
  employeeLoading: false,
});

const createEmployeeStart = (state: EmployeeStateType): EmployeeStateType => ({
  ...state,
  employeeCreateLoading: true,
});

const createEmployeeSuccess = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  employeeCreateLoading: false,
  employeeCreateError: null,
  employeeCreateSuccess: true,
  createdEmployee: action.createdEmployee,
});

const createEmployeeFail = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  employeeCreateLoading: false,
  employeeCreateError: action.employeeCreateError,
});

const updateEmployeeStart = (state: EmployeeStateType): EmployeeStateType => ({
  ...state,
  employeeUpdateLoading: true,
  employeeUpdateSuccess: false,
});

const updateEmployeeSuccess = (
  state: EmployeeStateType,
): EmployeeStateType => ({
  ...state,
  employeeUpdateLoading: false,
  employeeUpdateError: null,
  employeeUpdateSuccess: true,
});

const updateEmployeeFail = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  employeeUpdateLoading: false,
  employeeUpdateError: action.employeeUpdateError,
});

const deleteEmployeeStart = (state: EmployeeStateType): EmployeeStateType => ({
  ...state,
  employeeDeleteLoading: true,
});

const deleteEmployeeSuccess = (
  state: EmployeeStateType,
): EmployeeStateType => ({
  ...state,
  employeeDeleteLoading: false,
  employeeDeleteError: null,
  employeeDeleteSuccess: true,
  employeesListUpdateNeeded: true,
});

const deleteEmployeeFail = (
  state: EmployeeStateType,
  action: EmployeeActionType,
): EmployeeStateType => ({
  ...state,
  employeeDeleteLoading: false,
  employeeDeleteError: action.employeeDeleteError,
});

const refreshEmployeeList = (state: EmployeeStateType): EmployeeStateType => ({
  ...initialState,
  employeesListUpdateNeeded: true,
});

const resetEmployeeStore = (state: EmployeeStateType): EmployeeStateType => ({
  ...initialState,
});

const completelyResetEmployeeStore = (): EmployeeStateType => ({
  ...initialState,
});

const reducer = (state = initialState, action: EmployeeActionType) => {
  switch (action.type) {
    case actionTypes.FETCH_EMPLOYEES_START:
      return fetchEmployeesStart(state);
    case actionTypes.FETCH_EMPLOYEES_SUCCESS:
      return fetchEmployeesSuccess(state, action);
    case actionTypes.FETCH_EMPLOYEES_FAIL:
      return fetchEmployeesFail(state, action);
    case actionTypes.FETCH_ALL_EMPLOYEES_START:
      return fetchAllEmployeesStart(state);
    case actionTypes.FETCH_ALL_EMPLOYEES_SUCCESS:
      return fetchAllEmployeesSuccess(state, action);
    case actionTypes.FETCH_ALL_EMPLOYEES_FAIL:
      return fetchAllEmployeesFail(state, action);
    case actionTypes.FETCH_EMPLOYEE_START:
      return fetchEmployeeStart(state);
    case actionTypes.FETCH_EMPLOYEE_SUCCESS:
      return fetchEmployeeSuccess(state, action);
    case actionTypes.FETCH_EMPLOYEE_FAIL:
      return fetchEmployeeFail(state, action);
    case actionTypes.CREATE_EMPLOYEE_START:
      return createEmployeeStart(state);
    case actionTypes.CREATE_EMPLOYEE_SUCCESS:
      return createEmployeeSuccess(state, action);
    case actionTypes.CREATE_EMPLOYEE_FAIL:
      return createEmployeeFail(state, action);
    case actionTypes.UPDATE_EMPLOYEE_START:
      return updateEmployeeStart(state);
    case actionTypes.UPDATE_EMPLOYEE_SUCCESS:
      return updateEmployeeSuccess(state);
    case actionTypes.UPDATE_EMPLOYEE_FAIL:
      return updateEmployeeFail(state, action);
    case actionTypes.DELETE_EMPLOYEE_START:
      return deleteEmployeeStart(state);
    case actionTypes.DELETE_EMPLOYEE_SUCCESS:
      return deleteEmployeeSuccess(state);
    case actionTypes.DELETE_EMPLOYEE_FAIL:
      return deleteEmployeeFail(state, action);
    case actionTypes.REFRESH_EMPLOYEE_LIST:
      return refreshEmployeeList(state);
    case actionTypes.RESET_EMPLOYEE_STORE:
      return resetEmployeeStore(state);
    case actionTypes.LOGOUT:
      return completelyResetEmployeeStore();
    default:
      return state;
  }
};

export default reducer;
